import { cx } from "@jugl-web/utils";
import { forwardRef, ReactNode } from "react";
import { Drawer, DrawerProps } from "../Drawer";
import {
  BreadcrumbsDrawerHeaderProps,
  DrawerHeader,
  DrawerHeaderProps,
  TitleDrawerHeaderProps,
} from "../DrawerHeader";

type BottomCenteredDrawerSize = "md" | "fullscreen";

export interface BottomCenteredDrawerProps
  extends Pick<
    DrawerProps,
    "isOpen" | "onClose" | "onTransitionEnd" | "hasBackdrop" | "initialFocus"
  > {
  children: ReactNode;
  size?: BottomCenteredDrawerSize;
  header?: (TitleDrawerHeaderProps | BreadcrumbsDrawerHeaderProps) &
    Pick<DrawerHeaderProps, "navigationSlots">;
  className?: string;
}

const sizeToClasses: Record<BottomCenteredDrawerSize, string> = {
  md: "left-1/2 h-[90vh] w-[960px] -translate-x-1/2",
  fullscreen:
    "left-12 right-12 h-[95%] w-auto max-lg:inset-x-0 max-lg:h-full max-lg:rounded-none",
};

export const BottomCenteredDrawer = ({
  children,
  isOpen,
  size = "md",
  header,
  hasBackdrop,
  initialFocus,
  className,
  onClose,
  onTransitionEnd,
}: BottomCenteredDrawerProps) => (
  <Drawer
    isOpen={isOpen}
    size="auto"
    placement="bottom"
    hasBackdrop={hasBackdrop}
    onClose={onClose}
    onTransitionEnd={onTransitionEnd}
    initialFocus={initialFocus}
    className={cx(
      sizeToClasses[size],
      "flex flex-col overflow-clip rounded-t-2xl",
      className
    )}
  >
    {header && <DrawerHeader {...header} onClose={onClose} />}
    {children}
  </Drawer>
);

export interface BottomCenteredDrawerContentProps {
  children: ReactNode;
  id?: string;
  className?: string;
}

BottomCenteredDrawer.Content = forwardRef<
  HTMLDivElement,
  BottomCenteredDrawerContentProps
>(({ children, className, id }, ref) => (
  <div ref={ref} id={id} className={cx("grow overflow-y-auto", className)}>
    {children}
  </div>
));

export interface BottomCenteredDrawerActionsProps {
  children: ReactNode;
  className?: string;
}

BottomCenteredDrawer.Actions = ({
  children,
  className,
}: BottomCenteredDrawerActionsProps) => (
  <div
    className={cx(
      "flex h-[88px] shrink-0 items-center justify-center gap-2.5 border-t border-r-0 border-b-0 border-l-0 border-solid border-[#EEF2F5] bg-white px-8",
      className
    )}
  >
    {children}
  </div>
);
